/**
 * AwaitingSignaturesBanner — Wave 7 commit-pipeline UI.
 *
 * Surfaces when `cart.status === 'awaiting-signatures'`.
 *
 * 2026-07-02 rework (live-walk findings, Bill's cardinal rules):
 * - The banner is ENGINE-AWARE. In-branch ceremonies (internal / sandbox
 *   signing groups — the pilot default) show "sign with your customer in the
 *   documents section above" with NO invite/expiry language: nothing was
 *   emailed and nothing expires in days.
 * - The old "Resend signing invite" button is GONE — it posted
 *   /v3/carts/{id}/signing/resend-invite, an endpoint that never existed
 *   (live 404). When the remote-invite flow gets a real resend endpoint the
 *   remote variant can grow the button back.
 * - Remote (real SignNow) sessions keep the count + expiry copy.
 */

import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/primitives';
import type { CartDto } from '@retail-ops/shared-types';

export interface AwaitingSignaturesBannerProps {
  cart: CartDto;
  /** Live count of completed signatures (from `CommitStatus.signaturesCollected`). */
  signaturesCollected?: number;
  /** Total signatures required (from `CommitStatus.signaturesTotal`). */
  signaturesTotal?: number;
  /** Days until a REMOTE signing link expires. Ignored for in-person ceremonies. */
  signingExpiresInDays?: number;
  /** Jump the FSR to the signing section (SignStep). */
  onViewDetails?: (cart: CartDto) => void;
  /** @deprecated resend was never wired server-side; retained so parents compile. */
  onResent?: (sentTo: string | undefined) => void;
}

function pluralize(n: number, one: string, many: string): string {
  return n === 1 ? `1 ${one}` : `${n} ${many}`;
}

function buildCountSummary(collected?: number, total?: number): string {
  if (typeof total === 'number' && total > 0) {
    const c = typeof collected === 'number' ? collected : 0;
    return `${c} of ${total} ${total === 1 ? 'signature' : 'signatures'} collected`;
  }
  if (typeof collected === 'number' && collected > 0) {
    return `${pluralize(collected, 'signature', 'signatures')} collected`;
  }
  return 'No signatures collected yet';
}

function buildExpirySummary(daysLeft: number): string {
  if (daysLeft < 0) return 'Signing window expired';
  if (daysLeft === 0) return 'Expires today';
  if (daysLeft === 1) return 'Expires in 1 day';
  return `Expires in ${daysLeft} days`;
}

/** In-person = internal/sandbox signing group (the in-branch default). */
function isInPersonCeremony(cart: CartDto): boolean {
  const g = cart.signingGroupId ?? '';
  return g === '' || g.startsWith('internal-') || g.startsWith('sandbox-');
}

export function AwaitingSignaturesBanner({
  cart,
  signaturesCollected,
  signaturesTotal,
  signingExpiresInDays,
  onViewDetails,
}: AwaitingSignaturesBannerProps): JSX.Element {
  const inPerson = isInPersonCeremony(cart);

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-5)',
        padding: 'var(--sp-5) var(--sp-7)',
        background: 'var(--warn-bg)',
        color: 'var(--warn-text)',
        border: `1px solid var(--pcb-amber-text)`,
        borderRadius: 'var(--r-md)',
        fontSize: 12,
        lineHeight: 1.5,
      }}
    >
      <span aria-hidden style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}>
        <AlertTriangle size={16} aria-hidden />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 700, marginBottom: 2 }}>
          {inPerson ? 'Signatures in progress' : 'Waiting on the customer to sign'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--sp-4)', alignItems: 'center' }}>
          <span>{buildCountSummary(signaturesCollected, signaturesTotal)}</span>
          {inPerson ? (
            <span>— collect them with your customer in the signing section above.</span>
          ) : (
            typeof signingExpiresInDays === 'number' && (
              <>
                <span aria-hidden style={{ color: 'var(--ink-400)' }}>·</span>
                <span style={{ fontVariantNumeric: 'tabular-nums' }}>
                  {buildExpirySummary(signingExpiresInDays)}
                </span>
              </>
            )
          )}
        </div>
      </div>
      {onViewDetails && (
        <Button variant="primary" onClick={() => onViewDetails(cart)}>
          Go to signing ↑
        </Button>
      )}
    </div>
  );
}
